import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '~/utils/cn';

import { Icon } from '../../atoms/Icon/view';

export type TrialTier = 'calm' | 'soon' | 'urgent';

interface PropsTrialBubble {
  primaryLabel: string;
  secondaryLabel: string;
  tier: TrialTier;
  onOpen: () => void;
  onDismiss: () => void;
}

const SIZE = 62;
const MARGIN = 16;
const DRAG_THRESHOLD = 4;
const POS_KEY = 'pa:trial-widget:pos';

type Pos = { x: number; y: number };

const TIER_CLASS: Record<TrialTier, string> = {
  calm: 'bg-support-01 text-primary-01 border-support-02/40',
  soon: 'bg-alert-01 text-alert-06 border-alert-02',
  urgent: 'bg-alert-02 text-alert-07 border-alert-03/40 animate-pulse_soft',
};

function bottomReserve() {
  return window.innerWidth < 1280 ? 92 : MARGIN;
}

function clamp({ x, y }: Pos): Pos {
  const maxX = window.innerWidth - SIZE - MARGIN;
  const maxY = window.innerHeight - SIZE - bottomReserve();
  return {
    x: Math.min(Math.max(x, MARGIN), Math.max(maxX, MARGIN)),
    y: Math.min(Math.max(y, MARGIN), Math.max(maxY, MARGIN)),
  };
}

function readStoredPos(): Pos | null {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Pos;
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
  } catch {
  }
  return null;
}

function initialPos(): Pos {
  return clamp({
    x: window.innerWidth - SIZE - MARGIN,
    y: window.innerHeight * 0.5 + 32,
  });
}

export default function TrialBubble({
  primaryLabel,
  secondaryLabel,
  tier,
  onOpen,
  onDismiss,
}: PropsTrialBubble) {
  const elRef = useRef<HTMLDivElement>(null);
  const posRef = useRef<Pos>({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const [ready, setReady] = useState(false);

  const apply = useCallback(() => {
    if (elRef.current) {
      elRef.current.style.transform = `translate3d(${posRef.current.x}px, ${posRef.current.y}px, 0)`;
    }
  }, []);

  useEffect(() => {
    posRef.current = readStoredPos() ? clamp(readStoredPos()!) : initialPos();
    apply();
    setReady(true);

    const onResize = () => {
      posRef.current = clamp(posRef.current);
      apply();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [apply]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: posRef.current.x,
      originY: posRef.current.y,
      moved: false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) drag.moved = true;
    if (!drag.moved) return;
    posRef.current = clamp({ x: drag.originX + dx, y: drag.originY + dy });
    apply();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
    }
    if (drag.moved) {
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
      } catch {
      }
    } else {
      onOpen();
    }
  };

  const compactPrimary = primaryLabel.length > 2;

  return (
    <div
      ref={elRef}
      className="fixed left-0 top-0 z-40 touch-none select-none animate-fade_in"
      style={{ willChange: 'transform' }}
    >
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <div
          role="button"
          tabIndex={0}
          aria-label={`Período gratuito: ${primaryLabel} ${secondaryLabel} restantes. Abrir detalhes.`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen();
            }
          }}
          className={cn(
            'flex h-full w-full cursor-grab flex-col items-center justify-center rounded-full border shadow-level2 outline-none transition-shadow active:cursor-grabbing hover:shadow-level3 focus-visible:ring-2 focus-visible:ring-primary-01/40',
            TIER_CLASS[tier],
            ready ? 'opacity-100' : 'opacity-0',
          )}
        >
          <span
            className={cn(
              'font-bold leading-none',
              compactPrimary ? 'text-[13px]' : 'text-[22px]',
            )}
          >
            {primaryLabel}
          </span>
          <span className="mt-[2px] text-[10px] font-medium leading-none opacity-80">
            {secondaryLabel}
          </span>
        </div>

        <button
          type="button"
          aria-label="Dispensar"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onDismiss}
          className="absolute -right-[5px] -top-[5px] flex h-[20px] w-[20px] items-center justify-center rounded-full border border-support-02/50 bg-intense-01 opacity-50 shadow-level1 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-01/30"
        >
          <Icon name="icon-close" size={10} color="#808080" rounded={false} />
        </button>
      </div>
    </div>
  );
}
